import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";
import { useScrollAnimation } from "./hooks/useScrollAnimation";
import { useLogoAnimation } from "./hooks/useLogoAnimation";
import { ContactDialog } from "./ContactDialog";

export function HeroBanner() {
  useScrollAnimation();
  const { isScrolled } = useLogoAnimation();
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    title: "Get Started with Kartly",
    description:
      "Transform your business with AI-powered eCommerce solutions. Let's discuss how we can help you scale.",
    buttonText: "Get Started",
  });

  useEffect(() => {
    // Start animations immediately on load for better UX
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll(".hero-animate");
      elements.forEach((el, index) => {
        setTimeout(() => {
          el.classList.add("animate");
        }, index * 200);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const scrollToNext = () => {
    const nextSection = document.getElementById("about");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleGetStarted = () => {
    setDialogConfig({
      title: "Begin Your Journey with Kartly",
      description:
        "Ready to transform your business with AI-powered eCommerce? Let's start with a personalized consultation to understand your needs.",
      buttonText: "Begin Your Journey",
    });
    setContactDialogOpen(true);
  };

  const handleTryFree = () => {
    setDialogConfig({
      title: "Try Kartly Free",
      description:
        "Experience the power of intelligent eCommerce with our free trial. No commitment required - see the results for yourself.",
      buttonText: "Start Free Trial",
    });
    setContactDialogOpen(true);
  };

  return (
    <>
      <section
        id="home"
        className="relative min-h-screen overflow-hidden light-gradient-1 dark:bg-gray-950 transition-all duration-700"
      >
        {/* Enhanced floating background elements with dark mode adaptation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Dark mode overlay - darker for dark mode */}
          <div className="absolute inset-0 dark:bg-gradient-to-br dark:from-gray-950 dark:via-red-900/40 dark:to-orange-900/40 transition-all duration-700"></div>

          <div className="absolute top-20 left-10 w-80 h-80 bg-red-100 dark:bg-red-900/60 rounded-full blur-3xl opacity-15 dark:opacity-40 animate-pulse transition-all duration-700"></div>
          <div
            className="absolute bottom-20 right-10 w-80 h-80 bg-orange-100 dark:bg-orange-900/60 rounded-full blur-3xl opacity-15 dark:opacity-40 animate-pulse transition-all duration-700"
            style={{ animationDelay: "2s" }}
          ></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-red-50 to-orange-50 dark:from-gray-900/80 dark:to-orange-900/40 rounded-full blur-3xl opacity-25 dark:opacity-60 transition-all duration-700"></div>

          {/* Additional floating particles */}
          <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-red-200 dark:bg-red-900/40 rounded-full blur-2xl opacity-10 dark:opacity-30 float-animation transition-all duration-700"></div>
          <div
            className="absolute bottom-1/4 left-1/4 w-40 h-40 bg-orange-200 dark:bg-orange-900/40 rounded-full blur-2xl opacity-10 dark:opacity-30 float-animation transition-all duration-700"
            style={{ animationDelay: "3s" }}
          ></div>
          <div
            className="absolute top-3/4 right-1/3 w-24 h-24 bg-red-300 dark:bg-red-900/35 rounded-full blur-xl opacity-8 dark:opacity-30 float-animation transition-all duration-700"
            style={{ animationDelay: "5s" }}
          ></div>
        </div>

        {/* Content with enhanced spacing to avoid logo overlap */}
        <div className="relative z-20 flex items-center justify-center min-h-screen">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Content Container with proper spacing below logo */}
            <div
              className={`hero-content-container ${
                isScrolled ? "scrolled" : ""
              }`}
            >
              {/* Tagline - Now positioned below logo with enhanced spacing */}
              <div className="hero-animate fade-in-up mb-12">
                <div className="section-tag mx-auto">
                  <Sparkles className="text-red-500" size={16} />
                  Smarter Tech. Better Experience
                </div>
              </div>

              {/* Main Headline */}
              <div className="hero-animate fade-in-up mb-12">
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary-light leading-tight">
                  Intelligent eCommerce,
                  <br />
                  <span className="gradient-text">Elevated</span>
                </h1>
              </div>

              {/* Subtitle */}
              <div className="hero-animate fade-in-up mb-16">
                <p className="text-secondary-light text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                  Transform your business with cutting-edge AI technology and
                  data-driven insights. Experience the future of commerce with
                  unparalleled strength and sophistication.
                </p>
              </div>

              {/* Enhanced CTA Buttons */}
              <div className="hero-animate fade-in-up mb-20">
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <Button
                    size="lg"
                    onClick={handleGetStarted}
                    className="bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 text-lg px-12 py-4 shadow-xl hover:shadow-red-500/25 transition-all duration-700 hover:scale-105 border-0 btn-interactive"
                  >
                    Begin Your Journey
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                  {/* <Button 
                    variant="outline" 
                    size="lg" 
                    onClick={handleTryFree}
                    className="border-2 border-purple-200 dark:border-purple-400 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/50 text-lg px-12 py-4 transition-all duration-700 hover:scale-105 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm btn-interactive"
                  >
                    Try Free
                  </Button> */}
                </div>
              </div>

              {/* Enhanced Scroll Indicator */}
              <div className="hero-animate fade-in-up">
                <button
                  onClick={scrollToNext}
                  className="group text-muted-light hover:text-primary-light transition-all duration-700 animate-bounce hover:animate-none"
                >
                  <div className="flex flex-col items-center space-y-3">
                    <span className="text-sm font-medium opacity-60 group-hover:opacity-100 transition-all duration-500">
                      Discover More
                    </span>
                    <div className="w-1 h-8 bg-gradient-to-b from-red-300 to-orange-300 rounded-full opacity-40 group-hover:opacity-70 transition-all duration-500"></div>
                    <ChevronDown
                      size={32}
                      className="group-hover:transform group-hover:scale-110 transition-all duration-500"
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Dialog */}
      <ContactDialog
        isOpen={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        title={dialogConfig.title}
        description={dialogConfig.description}
        buttonText={dialogConfig.buttonText}
      />
    </>
  );
}
